import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { URL_MAP } from '../../shared/urlMap';
import { environment as ENV } from '../../../environments/environment';
import { TarifaDestino } from '../../models/tarifas-destino/tarifa-destino.model';

export interface TarifasDestinoRequest {
  prm_linea_negocio: string;
  /** Cambio de cobertura: texto del plan destino seleccionado (ej. `FAMILIAR`, `ACU|ACUERDO CORPORATI`). */
  prm_tipo_plan: string;
  /** Cambio de cobertura: texto del programa destino seleccionado (ej. `ORO | ORO`). */
  prm_programa: string;
}

export interface TarifaCotizadorTrlRequest {
  prm_cuenta: string;
  prm_subcta: string;
  prm_plan_codi: string;
  prm_tari: string;
  prm_edad: string;
  prm_fecha: string;
  vigen_desde: string;
  prm_paren: string;
  prm_sexo: string;
  prm_inte_tipo: string;
  cant_inte: string;
}

@Injectable({
  providedIn: 'root',
})
export class TarifasDestinoService {
  private apiUrl = `${ENV.HOST}${URL_MAP.API.TARIFAS_DESTINO}`;
  private apiCotizadorTrlUrl = `${ENV.HOST}${URL_MAP.API.CAMBIO_TARIFA_COTIZADOR_TRL}`;

  constructor(private http: HttpClient) {}

  getTarifasDestino(
    params: TarifasDestinoRequest
  ): Observable<TarifaDestino[]> {
    const body = {
      jsonParametros: JSON.stringify(params),
    };

    return this.http.post<any>(this.apiUrl, body).pipe(
      map((response) =>
        this.extraerTarifasArray(response).map((item: any) => ({
          codigo: (item.codigo ?? '').toString(),
          descripcion: item.descripcion ?? '',
          idSubcuenta: (item.idSubcuenta ?? item.subcuenta ?? item.subcta ?? '')
            .toString()
            .trim(),
          nombre: item.descripcion ?? item.nombre ?? '',
          valorTarifa:
            Number(
              item.valorTarifa ??
                item.valor_tarifa ??
                item.valor ??
                item.VALOR ??
                item.tarifa ??
                item.valorTarifaPrograma ??
                0
            ) || 0,
        }))
      )
    );
  }

  getTarifasCambioTarifa(
    params: TarifasDestinoRequest
  ): Observable<TarifaDestino[]> {
    const body = {
      jsonParametros: JSON.stringify(params),
    };

    return this.http.post<any>(this.apiUrl, body).pipe(
      map((response) => {
        const items = this.extraerTarifasArray(response);
        return items.map((item: any) => ({
          codigo: (item.codigo ?? '').toString(),
          descripcion: item.descripcion ?? '',
          idSubcuenta: (item.idSubcuenta ?? item.subcuenta ?? item.subcta ?? '')
            .toString()
            .trim(),
          nombre: item.descripcion ?? item.nombre ?? '',
          valorTarifa:
            Number(
              item.valorTarifa ??
                item.valor_tarifa ??
                item.valor ??
                item.VALOR ??
                item.tarifa ??
                item.valorTarifaPrograma ??
                0
            ) || 0,
        }));
      })
    );
  }

  getValorTarifaCotizadorTrl(
    params: TarifaCotizadorTrlRequest
  ): Observable<number | null> {
    const body = {
      nombreClave: 'getTarifasCotizadortrl',
      jsonParametros: JSON.stringify(params),
    };
    return this.http.post<any>(this.apiCotizadorTrlUrl, body).pipe(
      map((response) => {
        const item = this.extraerPrimerItemArray(response);
        if (!item) {
          return null;
        }
        const valor = Number(item.valor ?? item.Valor ?? item.VALOR ?? NaN);
        return Number.isFinite(valor) ? valor : null;
      })
    );
  }

  private extraerTarifasArray(raw: unknown): any[] {
    const seen = new WeakSet<object>();

    const parseJsonFlexible = (s: string): unknown => {
      const t = s.trim();
      if (!t) {
        return null;
      }
      try {
        return JSON.parse(t);
      } catch {
        // Algunos gateways devuelven `"tarifas":[...]` sin llaves.
        if (t.includes('"tarifas"') && !t.startsWith('{')) {
          try {
            return JSON.parse(`{${t}}`);
          } catch {
            return null;
          }
        }
        return null;
      }
    };

    const walk = (v: unknown, depth: number): any[] => {
      if (v == null || depth > 8) {
        return [];
      }
      if (Array.isArray(v)) {
        return v;
      }
      if (typeof v === 'string') {
        const parsed = parseJsonFlexible(v);
        return parsed != null ? walk(parsed, depth + 1) : [];
      }
      if (typeof v !== 'object') {
        return [];
      }
      const o = v as Record<string, unknown>;
      if (seen.has(o)) {
        return [];
      }
      seen.add(o);

      const direct = o['tarifas'] ?? o['Tarifas'] ?? o['items'] ?? o['lista'];
      if (Array.isArray(direct)) {
        return direct;
      }

      for (const key of [
        'data',
        'Data',
        'resultado',
        'Resultado',
        'response',
        'Response',
        'result',
        'Result',
        'jsonRespuesta',
        'JsonRespuesta',
        'body',
        'Body',
        'content',
        'Content',
      ]) {
        const got = walk(o[key], depth + 1);
        if (got.length > 0) {
          return got;
        }
      }
      return [];
    };

    return walk(raw, 0);
  }

  private extraerPrimerItemArray(raw: unknown): any | null {
    if (Array.isArray(raw)) {
      return raw.length > 0 ? raw[0] : null;
    }
    if (raw && typeof raw === 'object') {
      const o = raw as Record<string, unknown>;
      for (const key of ['data', 'Data', 'content', 'Content', 'resultado', 'Result']) {
        const v = o[key];
        if (Array.isArray(v) && v.length > 0) {
          return v[0];
        }
      }
    }
    return null;
  }
}
